import { type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useAuth } from '@/auth/AuthProvider'
import { useTheme } from '@/theme/ThemeProvider'
import { AppIcon, type AppIconName } from '@/components/ui/AppIcon'

// Same icons as the mobile tab bar: today / chef-hat / stats-chart / settings.
// Saved Foods (heart) is web-only — mobile reaches it via the Quick Add sheet.
const NAV: { to: string; icon: AppIconName; labelKey: string; fallback: string }[] = [
  { to: '/diary', icon: 'today', labelKey: 'settings.diary', fallback: 'Diary' },
  { to: '/recipes', icon: 'chef-hat', labelKey: 'recipes.titleShort', fallback: 'Recipes' },
  { to: '/saved-foods', icon: 'heart', labelKey: 'savedFoods.title', fallback: 'Saved Foods' },
  { to: '/shopping-list', icon: 'cart', labelKey: 'shopping.list.title', fallback: 'Shopping List' },
  { to: '/progress', icon: 'stats-chart', labelKey: 'progress.title', fallback: 'Progress' },
  { to: '/settings', icon: 'settings', labelKey: 'settings.title', fallback: 'Settings' },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { colorScheme, setPreference } = useTheme()

  const navItems = NAV.map((item) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-md rounded-lg px-md py-sm text-body font-medium transition-colors',
          isActive
            ? 'bg-bg-secondary text-primary'
            : 'text-text-secondary hover:bg-bg-secondary hover:text-text',
        )
      }
    >
      {({ isActive }) => (
        <>
          <AppIcon name={item.icon} filled={isActive} />
          <span>{t(item.labelKey, item.fallback)}</span>
        </>
      )}
    </NavLink>
  ))

  return (
    <div className="flex h-full w-full flex-col bg-bg md:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="no-print hidden w-64 shrink-0 flex-col border-r border-border bg-bg-elevated p-md md:flex">
        <div className="mb-lg flex items-center gap-sm px-sm pt-sm">
          <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-[10px]" />
          <span className="text-h4 font-bold text-text">KidneyPal</span>
        </div>

        <button
          onClick={() => navigate('/log')}
          className="mb-md flex items-center justify-center gap-sm rounded-lg bg-primary px-md py-sm text-button font-semibold text-white hover:opacity-90"
        >
          <AppIcon name="add" size={20} /> {t('diary.addFood.title', 'Log food')}
        </button>

        <nav className="flex flex-1 flex-col gap-xs">{navItems}</nav>

        <div className="mt-md border-t border-border pt-md">
          <button
            onClick={() => setPreference(colorScheme === 'dark' ? 'light' : 'dark')}
            className="mb-sm flex w-full items-center gap-md rounded-lg px-md py-sm text-body-sm text-text-secondary hover:bg-bg-secondary"
          >
            {colorScheme === 'dark' ? '☀️' : '🌙'}{' '}
            {t('settings.theme.title', 'Theme')}
          </button>
          <p className="truncate px-md text-caption text-text-muted" title={user?.email ?? ''}>
            {user?.email}
          </p>
          <button
            onClick={() => void signOut()}
            className="mt-xs w-full rounded-lg px-md py-sm text-left text-body-sm text-text-secondary hover:bg-bg-secondary hover:text-trigger"
          >
            {t('settings.signOut', 'Sign Out')}
          </button>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="no-print flex items-center justify-between border-b border-border bg-bg-elevated px-md py-sm md:hidden">
        <span className="flex items-center gap-sm text-h4 font-bold text-text">
          <img src="/icon-192.png" alt="" className="h-7 w-7 rounded-lg" />
          KidneyPal
        </span>
        <button
          onClick={() => void signOut()}
          className="text-body-sm text-text-secondary"
        >
          {t('settings.signOut', 'Sign Out')}
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl px-md py-lg md:px-xl">{children}</div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="no-print flex items-center justify-around border-t border-border bg-bg-elevated md:hidden">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              clsx(
                'flex flex-1 flex-col items-center gap-xs py-sm text-caption',
                isActive ? 'text-primary' : 'text-text-muted',
              )
            }
          >
            {({ isActive }) => (
              <>
                <AppIcon name={item.icon} filled={isActive} />
                <span>{t(item.labelKey, item.fallback)}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
